import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";

import { useFormik } from "formik";

import { toast } from "react-toastify";
import { BsUpload } from "react-icons/bs";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import { useLayout } from "@/../_metronic/layout/core";
import { registerTrackOrder } from "@/services/app/trackOrderService";
import {
	getUrlUploadToAzure,
	registerTrackFile,
	TrackFileFormData,
	uploadVideoWithProgress,
} from "@/services/app/trackFileService";
import { extractBlobPath } from "@/utils/app/extractBlobPath";
import { useVideoDuration } from "@/hooks/app/useVideoDuration";

import { CardDropzone } from "@/components/CardDropzone";
import { LoadingScreen } from "@/components/LoadingScreen";

export function FormTrackFile() {
	const { setTitle } = useLayout();
	const { trackListId } = useParams();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [progress, setProgress] = useState(0);

	const formik = useFormik<TrackFileFormData>({
		initialValues: {} as TrackFileFormData,
		onSubmit: handleRegisterTrackFile,
	});

	useVideoDuration({
		file: formik.values.videoFile,
		onDuration: (duration) => formik.setFieldValue("duration", duration),
	});

	async function uploadVideo(file: File) {
		if (!file) {
			return;
		}

		const response = await getUrlUploadToAzure(formik.values.title);
		const uploadUrl = response.data?.uploadUrl;
		if (response.status !== 200 || !uploadUrl) {
			toast.error("Falha ao gerar link para upload");
			return;
		}

		if (uploadUrl) {
			const hasCompletedUpload = await uploadVideoWithProgress(file, uploadUrl, (percent) => {
				setProgress(percent);
			});
			if (hasCompletedUpload) {
				return extractBlobPath(uploadUrl);
			}
		}
	}

	async function handleRegisterTrackFile(trackFileFormData: TrackFileFormData) {
		setIsLoading(true);
		try {
			const urlVideo = await uploadVideo(trackFileFormData.videoFile);
			if (!urlVideo) {
				throw new Error();
			}

			const registeredTrackFile = await registerTrackFile({
				...trackFileFormData,
				urlVideo,
			});

			if (
				!trackFileFormData.videoFile ||
				!trackFileFormData.thumbnailFile ||
				!registeredTrackFile.id
			) {
				throw new Error();
			}

			const isRegisteredTrackOrder = await registerTrackOrder(
				Number(trackListId),
				registeredTrackFile.id
			);

			if (!isRegisteredTrackOrder) {
				toast.error("Erro ao registrar track order");
			}

			toast.success("Arquivos registrados com sucesso");
			formik.resetForm();
			navigate(-1);
		} catch {
			toast.error("Ocorreu um erro no envio");
		}
		setIsLoading(false);
	}

	useEffect(() => {
		setTitle("GERENCIAMENTO DE PLAYLIST");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<section className="bg-white p-4">
			<LoadingScreen
				loading={isLoading}
				personalLoader={
					<section className="d-flex flex-column justify-content-center align-items-center w-100">
						<progress className="w-100" value={progress} max={100} />
						<span>{progress.toFixed(2)}%</span>
					</section>
				}
			/>
			<Form onSubmit={formik.handleSubmit} encType="multipart/form-data">
				<div className="d-flex my-3 justify-content-between w-100">
					<h1 className="text-primary fs-5">Upload de video para playlist</h1>
					<Button className="ml-auto" type="submit" disabled={isLoading}>
						<BsUpload className="me-2" /> Upload
					</Button>
				</div>
				<Row className="my-3">
					<Form.Group>
						<Form.Label className="fs-6">Título:</Form.Label>
						<Form.Control
							type="text"
							name="title"
							value={formik.values.title}
							onChange={formik.handleChange}
							required
						/>
					</Form.Group>
					<Form.Group className="mt-2">
						<Form.Label className="fs-6">Descrição:</Form.Label>
						<Form.Control
							type="text"
							name="description"
							value={formik.values.description}
							onChange={formik.handleChange}
							as="textarea"
							rows={3}
							required
						/>
					</Form.Group>
				</Row>
				<Row>
					<Col lg={4}>
						<Form.Group className="text-center bg-white">
							<Form.Label className="text-warning fs-5">
								<strong>Arquivo de Vídeo</strong>
							</Form.Label>

							<CardDropzone
								inputName="videoFile"
								description="Solte o arquivo de video aqui, para fazer o upload."
								acceptedFileType={{ "video/*": [".mp4", ".svi", ".mkv"] }}
								descriptionAcceptedFilesExtension="Upload somente de arquivo .mp4/.avi (tamanho máximo recomendado: 500MB)"
								handleChange={(file: File) =>
									formik.setFieldValue("videoFile", file)
								}
							/>
						</Form.Group>
					</Col>
					<Col lg={4}>
						<Form.Group className="text-center bg-white">
							<Form.Label className="text-warning fs-5">
								<strong>Arquivo da thumbnail</strong>
							</Form.Label>
							<CardDropzone
								inputName="thumbnailFile"
								description="Solte o arquivo de thumbnail aqui, para fazer o upload."
								acceptedFileType={{ "image/*": [".jpeg", ".png"] }}
								descriptionAcceptedFilesExtension="Upload somente de arquivo .jpg/.png (tamanho máximo: 10MB)"
								handleChange={(file: File) =>
									formik.setFieldValue("thumbnailFile", file)
								}
							/>
						</Form.Group>
					</Col>
					<Col lg={4}>
						<Form.Group className="text-center bg-white">
							<Form.Label className="text-warning fs-5">
								<strong>Arquivo de imagem de capa alterativa</strong>
							</Form.Label>
							<CardDropzone
								inputName="imageFile"
								description="Solte o arquivo alternativo aqui, para fazer o upload."
								acceptedFileType={{ "image/*": [".jpeg", ".png"] }}
								descriptionAcceptedFilesExtension="Upload somente de arquivo .jpg/.png (tamanho máximo: 10MB)"
								handleChange={(file: File) =>
									formik.setFieldValue("imageFile", file)
								}
							/>
						</Form.Group>
					</Col>
				</Row>
			</Form>
		</section>
	);
}
